import React, { useEffect, useState } from 'react';
import AppBannerModalModal from './components/AppBannerModal';
import { AppBanner, PointOfSale } from '@m4a3/api-client';
import { PagedResponse } from '@m4a3/api-client/dist/models/shared/paged-response.interface';
import EmptyStateComponent from '../../components/EmptyState/EmptyState';
import { PencilIcon, MagnifyingGlassIcon } from '@heroicons/react/20/solid';
import { getPointOfSales } from '../../actions/adminActions';
import { RootState } from '../../store/configureStore';
import { connect } from 'react-redux';
import { logToDatadog } from '../../services/datadog';
import { orderfulApi } from '../../services/api-instances';

interface AppBannersProps {
  pointOfSales: PointOfSale[];
  cellId: number;
  timezone: string;
  getPointofSales: (cellId: number) => void;
}

function AppBanners({ pointOfSales, cellId, timezone, getPointofSales }: AppBannersProps) {
  const [shouldShowBannerMondal, setShouldShowBannerMondal] = useState(false);
  const [appBannerToEdit, setAppBannerToEdit] = useState<AppBanner>(null);
  const [appBanners, setAppBanners] = useState<AppBanner[]>([]);
  const [isLoadingAppBanners, setIsLoadingAppBanners] = useState(true);
  
  // Filtering states
  const [filteredAppBanners, setFilteredAppBanners] = useState<AppBanner[]>([]);
  const [filterDisplayFrom, setFilterDisplayFrom] = useState<string>('');
  const [filterDisplayUntil, setFilterDisplayUntil] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState<string>('');
  
  // Image hover state
  const [hoveredImageId, setHoveredImageId] = useState<string | null>(null);
  const [hoverPosition, setHoverPosition] = useState<'above' | 'below'>('below');
  
  // Function to determine if hover should show above or below
  const handleMouseEnter = (id: string, event: React.MouseEvent) => {
    setHoveredImageId(id);
    
    // Get position info to determine if we're near the bottom of the viewport
    const rect = event.currentTarget.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const bottomSpace = viewportHeight - rect.bottom;
    
    // If we have less than 220px at the bottom (image height + margin), show above
    if (bottomSpace < 220) {
      setHoverPosition('above');
    } else {
      setHoverPosition('below');
    }
  };

  useEffect(() => {
    setIsLoadingAppBanners(true);
    getPointofSales(cellId);
    getAndSetAppBannersForCell(cellId);
  }, [cellId]);
  
  // Effect to filter banners when filters or banner list changes
  useEffect(() => {
    applyFilters();
  }, [appBanners, filterDisplayFrom, filterDisplayUntil, searchTerm, pointOfSales]);

  const getAndSetAppBannersForCell = async (cellId: number) => {
    orderfulApi.getAllAppBannersInCell({limitToCellId: cellId, onlyShowActiveBanners: false, size: 500})
      .then((pagedAppBanners: PagedResponse<AppBanner>) => {
        setAppBanners(pagedAppBanners.entities);
        setFilteredAppBanners(pagedAppBanners.entities);
        setIsLoadingAppBanners(false);
      })
      .catch((err) => {
        logToDatadog('error_loading_app_banners', {err}, 'error');
        setIsLoadingAppBanners(false);
      })
  };
  
  // Find merchant IDs that match the search term
  const getMatchingMerchantIds = (searchStr: string): number[] => {
    if (!searchStr || !pointOfSales || pointOfSales.length === 0) return [];
    
    const normalizedSearch = searchStr.toLowerCase().trim();
    return pointOfSales
      .filter(pos => pos.posName.toLowerCase().includes(normalizedSearch))
      .map(pos => pos.id);
  };
  
  const applyFilters = () => {
    let filtered = [...appBanners];
    
    // Filter by date range
    if (filterDisplayFrom) {
      const fromDate = new Date(filterDisplayFrom);
      filtered = filtered.filter(banner => {
        const bannerFrom = new Date(banner.displayFrom);
        return bannerFrom >= fromDate;
      });
    }
    
    if (filterDisplayUntil) {
      const untilDate = new Date(filterDisplayUntil);
      filtered = filtered.filter(banner => {
        const bannerUntil = new Date(banner.displayUntil);
        return bannerUntil <= untilDate;
      });
    }
    
    // Filter by point of sale search term
    if (searchTerm) {
      const matchingMerchantIds = getMatchingMerchantIds(searchTerm);
      if (matchingMerchantIds.length > 0) {
        filtered = filtered.filter(banner => 
          matchingMerchantIds.includes(banner.merchantId)
        );
      }
    }
    
    setFilteredAppBanners(filtered);
  };

  const resetFilters = () => {
    setFilterDisplayFrom('');
    setFilterDisplayUntil('');
    setSearchTerm('');
  };

  const getBodyContent = (): JSX.Element => {
    if (appBanners?.length === 0 && !isLoadingAppBanners) {
      return (<EmptyStateComponent entityName='App banner' onClickCta={() => {
        setShouldShowBannerMondal(true);
      }} />);
    }
    return getAppBannersTable();
  };

  const getAppBannersTable = () => {
    return (<div className="px-4 sm:px-6 lg:px-8 mt-8 flow-root">
      <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
        <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
          <table className="min-w-full divide-y divide-gray-300">
            <thead>
              <tr>
                <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-0">
                  Image
                </th>
                <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                  Point of Sale
                </th>
                <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                  Display from
                </th>
                <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                  Display until
                </th>
                <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                  Priority
                </th>
                <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                  Edit
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredAppBanners?.map((appBanner) => {
                // Find the matching point of sale
                const pointOfSale = pointOfSales?.find(pos => pos.id === appBanner.merchantId);
                const posName = pointOfSale ? pointOfSale.posName : 'Unknown';
                
                return (
                <tr key={appBanner.id}>
                  <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-0 relative">
                    <div 
                      className="relative cursor-pointer" 
                      onMouseEnter={(e) => handleMouseEnter(appBanner.id, e)} 
                      onMouseLeave={() => setHoveredImageId(null)}
                    >
                      <img 
                        className='h-38 w-38 object-cover object-center' 
                        style={{ maxHeight: '38px', maxWidth: '38px' }} 
                        src={appBanner.backgroundImageUri} 
                        alt="App banner preview"
                      />
                      {hoveredImageId === appBanner.id && (
                        <div 
                          className={`absolute z-10 left-0 ${hoverPosition === 'above' ? 'bottom-full mb-2' : 'top-full mt-2'}`}
                        >
                          <img 
                            className='shadow-lg rounded border-2 border-gray-200' 
                            style={{ maxHeight: '200px', maxWidth: '300px' }}
                            src={appBanner.backgroundImageUri} 
                            alt="App banner full preview"
                          />
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                    {posName}
                  </td>
                  <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                    {new Date(appBanner.displayFrom).toLocaleString()}
                  </td>
                  <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                    {new Date(appBanner.displayUntil).toLocaleString()}
                  </td>
                  <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                    {appBanner.priority !== null ? appBanner.priority : '-'}
                  </td>
                  <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                    <div className="flex md:ml-4">
                      <button
                        onClick={() => {
                          setAppBannerToEdit(appBanner);
                          setShouldShowBannerMondal(true);
                        }}
                        className="ml-auto flex items-center gap-x-1 rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600"
                      >
                        <PencilIcon className="-ml-1.5 h-3 w-3" aria-hidden="true" />
                        Edit banner
                      </button>
                    </div>
                  </td>
                </tr>
              )})}
            </tbody>
          </table>
        </div>
      </div>
    </div>);
  };

  return (
    <div className='bg-white w-full'>
      <header className="flex flex-none items-center justify-between border-b border-gray-200 px-6 py-4">
        <div>
          <h1 className="text-base font-semibold leading-6 text-gray-900">App Banner</h1>
        </div>
        <div className="flex items-center">
          <div className="hidden md:ml-4 md:flex md:items-center">
            <button
              type="button"
              className="flex items-center gap-x-1.5 rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
              onClick={() => {
                setShouldShowBannerMondal(true);
              }}
            >
              New Banner
            </button>
          </div>
        </div>
      </header>
      
      {/* Filter Section */}
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Search field */}
          <div>
            <label htmlFor="search-point-of-sale" className="block text-sm font-medium text-gray-700">
              Search Point of Sale
            </label>
            <div className="mt-1 relative rounded-md shadow-sm">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
              </div>
              <input
                type="text"
                id="search-point-of-sale"
                className="block w-full pl-10 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                placeholder="Search by POS name"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
          
          <div>
            <label htmlFor="filter-display-from" className="block text-sm font-medium text-gray-700">Display From</label>
            <div className="mt-1 relative rounded-md shadow-sm">
              <input
                type="date"
                id="filter-display-from"
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                value={filterDisplayFrom}
                onChange={(e) => setFilterDisplayFrom(e.target.value)}
              />
            </div>
          </div>
          
          <div>
            <label htmlFor="filter-display-until" className="block text-sm font-medium text-gray-700">Display Until</label>
            <div className="mt-1 relative rounded-md shadow-sm">
              <input
                type="date"
                id="filter-display-until"
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                value={filterDisplayUntil}
                onChange={(e) => setFilterDisplayUntil(e.target.value)}
              />
            </div>
          </div>
          
          <div className="flex items-end">
            <button
              type="button"
              className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              onClick={resetFilters}
            >
              Reset Filters
            </button>
            
            <div className="ml-3 text-sm text-gray-500">
              {filteredAppBanners.length} of {appBanners.length} banners
            </div>
          </div>
        </div>
      </div>
      
      <div>
        {getBodyContent()}
      </div>
      {shouldShowBannerMondal ? <AppBannerModalModal
        pointOfSales={pointOfSales}
        appBanner={appBannerToEdit}
        cellId={cellId}
        timezone={timezone}
        onClose={() => {
          setShouldShowBannerMondal(false);
          setAppBannerToEdit(null);
          getAndSetAppBannersForCell(cellId);
        }} /> : <></>
      }
    </div>
  );
}

const mapStateToProps = (state: RootState) => {
  return {
    pointOfSales: state.admin.pointOfSales,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    getPointofSales: (cellId: number) => dispatch(getPointOfSales(cellId)),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(AppBanners);